import { type Participant } from '@clubspark-node/clubspark-tennis-types';
import { deepEqual } from 'fast-equals';
import { type Atom, useAtomValue } from 'jotai';
import { atomFamily, selectAtom } from 'jotai/utils';
import { participantConstants } from 'tods-competition-factory';

import { tournamentAtom, tournamentsAtom } from 'atoms/tournament';
import { type EventInterface, type ParticipantInterface } from 'typedefs/store/tmxTypes';

const { SIGNED_IN, SIGN_IN_STATUS } = participantConstants;

const hydrateParticipants = ({ participants, events }: { participants: Participant[]; events: EventInterface[] }) => {
  // Here be mutable code, charged to roam these parts by the council of performance. Proceed with caution.
  const hydratedParticipants = new Map<string, ParticipantInterface>();
  for (const participant of participants as ParticipantInterface[]) {
    const hydratedParticipant = {
      ...participant,
      events: [],
      individualParticipants: [],
      signedIn:
        (participant.timeItems ?? [])
          ?.filter(({ itemType }) => itemType === SIGN_IN_STATUS)
          .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())?.[0]?.itemValue ===
        SIGNED_IN,
    };
    hydratedParticipants.set(hydratedParticipant.participantId, hydratedParticipant);
  }
  for (const event of events ?? emptyEvents) {
    for (const { participantId, ...entry } of event.entries ?? emptyParticipants) {
      if (participantId) {
        const participant = hydratedParticipants.get(participantId);
        if (participant) {
          participant.events.push({
            ...event,
            ...entry,
          });
        }
        if (participant?.individualParticipantIds) {
          participant.individualParticipantIds.forEach((pId) => {
            const participant = hydratedParticipants.get(pId);
            participant.events.push({
              ...event,
              ...entry,
            });
          });
        }
      }
    }
  }
  hydratedParticipants.forEach((participant) => {
    if (participant?.individualParticipantIds) {
      for (const individualParticipantId of participant.individualParticipantIds) {
        participant.individualParticipants.push(hydratedParticipants.get(individualParticipantId));
      }
    }
  });
  return [...hydratedParticipants.values()];
};

const emptyParticipants: ParticipantInterface[] = [];
const emptyEvents: EventInterface[] = [];
export const tournamentParticipantsAtom = selectAtom(
  selectAtom(
    tournamentAtom,
    (tournament) => ({
      participants: tournament?.participants ?? emptyParticipants,
      events: tournament?.events?.map((event) => ({ tournamentId: tournament?.tournamentId, ...event })) ?? emptyEvents,
    }),
    deepEqual,
  ),
  hydrateParticipants,
);
export const tournamentsParticipantsAtom = selectAtom(
  selectAtom(
    tournamentsAtom,
    (tournaments) => {
      const tournamentsArray = Object.values(tournaments);
      return {
        participants: tournamentsArray.flatMap<Participant>(
          (tournament) => tournament?.participants ?? emptyParticipants,
        ),
        events: tournamentsArray.flatMap<EventInterface>(
          (tournament) =>
            tournament?.events?.map((event) => ({ tournamentId: tournament?.tournamentId, ...event })) ?? emptyEvents,
        ),
      };
    },
    deepEqual,
  ),
  hydrateParticipants,
);

export const tournamentParticipantsMapAtom: Atom<{
  [key: string]: ParticipantInterface;
}> = selectAtom(
  tournamentParticipantsAtom,
  (participants) =>
    participants?.reduce((acc, participant) => ({ ...acc, [participant.participantId]: participant }), {}) ?? {},
);

const useTournamentParticipants = () => ({
  tournamentParticipants: useAtomValue<ParticipantInterface[]>(tournamentParticipantsAtom),
  tournamentParticipantsMap: useAtomValue(tournamentParticipantsMapAtom),
});

export const useCompetitionParticipants = () => useAtomValue<ParticipantInterface[]>(tournamentsParticipantsAtom);

const competitionParticipantsAtomFamily = atomFamily((participantId: string) =>
  selectAtom(tournamentsParticipantsAtom, (tournamentsParticipants) =>
    tournamentsParticipants.find((participant) => participant.participantId === participantId),
  ),
);

export const useCompetitionParticipant = (participantId: string) =>
  useAtomValue(competitionParticipantsAtomFamily(participantId));

export default useTournamentParticipants;
